// src/pages/profile-settings/patient/Medications.jsx
import React from "react";
import {
  Search as SearchIcon,
  Plus,
  CircleX as CircleXIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Card from "@/components/ui/Card.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import TapButton from "@/pages/onboarding/_shared/TapButton.jsx";
import { loadMedications } from "@/utils/medications";
import { shouldHideUntranslatedCatalog } from "@/utils/i18nGuardrails.js";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { PATIENT_SETTINGS_PROFILE_ROUTE } from "@/pages/profile-settings/settingsNavigation.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import {
  loadPatientIntroPreview,
  savePatientIntroPreview,
} from "@/lib/patientIntroPreview";

const COMPLETION_SAFE_MEDICATIONS = ["None", "Other"];

const FRENCH_SAFE_MEDICATIONS = {
  None: "Aucun",
  Other: "Autre",
};

function isFrenchLanguageCode(languageCode) {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function getMedicationDisplayLabel(value, languageCode) {
  const text = String(value || "").trim();
  if (!isFrenchLanguageCode(languageCode)) return text;
  return FRENCH_SAFE_MEDICATIONS[text] || text;
}

/**
 * Patient Settings → Medications
 * - Similar to StepMedications, but in Settings shell
 * - Ghost Save on entry, wakes when selection changes
 * - Unsaved-changes confirm
 */
export default function MedicationsSettings({
  value = [], // array from profile
  onChange,
}) {
  const nav = useNavigate();
  const { refreshProfile } = useAuth() || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const [all, setAll] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [ehrSelectedKey, setEhrSelectedKey] = React.useState("[]");

  const introPreview = React.useMemo(() => loadPatientIntroPreview() || {}, []);
  const previewSelected = React.useMemo(() => {
    const raw = introPreview.medications;
    if (Array.isArray(raw)) return raw.map((item) => String(item || "").trim()).filter(Boolean);
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }, [introPreview]);

  // normalise initial selection
  const initialSelected = React.useMemo(
    () => {
      const propList = Array.isArray(value) ? value.slice() : [];
      if (propList.length > 0) return propList;
      return previewSelected;
    },
    [value, previewSelected]
  );
  const initialKey = React.useMemo(
    () => JSON.stringify([...initialSelected].sort()),
    [initialSelected]
  );

  const [localSelected, setLocalSelected] = React.useState(initialSelected);
  const [seedSelected, setSeedSelected] = React.useState(initialSelected);
  const [showConfirmLeave, setShowConfirmLeave] = React.useState(false);

  // keep local in sync when props change
  React.useEffect(() => {
    setSeedSelected(initialSelected);
    setLocalSelected(initialSelected);
  }, [initialKey]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDb() {
      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user?.id) return;

        const [{ data, error }, { data: ehrRow, error: ehrError }] = await Promise.all([
          supabase
            .from("patient_profiles")
            .select("*")
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("ehr_patient_info")
            .select("current_medications")
            .eq("patient_id", user.id)
            .maybeSingle(),
        ]);

        if (error || ehrError || !alive) return;

        const fromDbRaw = data?.current_medications ?? data?.medications ?? [];
        const fromDb = Array.isArray(fromDbRaw)
          ? fromDbRaw.map((item) => String(item || "").trim()).filter(Boolean)
          : typeof fromDbRaw === "string"
          ? fromDbRaw
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [];

        const fromEhrRaw = ehrRow?.current_medications ?? [];
        const fromEhr = Array.isArray(fromEhrRaw)
          ? fromEhrRaw.map((item) => String(item || "").trim()).filter(Boolean)
          : typeof fromEhrRaw === "string"
          ? fromEhrRaw
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
          : [];

        setEhrSelectedKey(JSON.stringify([...fromEhr].sort()));

        if (fromDb.length > 0) {
          setSeedSelected(fromDb);
          setLocalSelected(fromDb);
          savePatientIntroPreview({
            ...(loadPatientIntroPreview() || {}),
            medications: fromDb,
          });
        }
      } catch {
        // non-fatal
      }
    }

    hydrateFromDb();
    return () => {
      alive = false;
    };
  }, []);

  // load medications once
  React.useEffect(() => {
    let alive = true;
    (async () => {
      const names = await loadMedications();
      if (!alive) return;
      setAll(names);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const CHUNK = 60;
  const [visible, setVisible] = React.useState(CHUNK);
  const sentRef = React.useRef(null);

  const deferredQuery = React.useDeferredValue(query);
  const q = deferredQuery.trim().toLowerCase();

  const options = React.useMemo(() => {
    const seen = new Set();
    const next = [];
    for (const option of [
      ...COMPLETION_SAFE_MEDICATIONS,
      ...all,
      ...seedSelected,
      ...localSelected,
    ]) {
      const canonical = String(option || "").trim();
      const key = canonical.toLowerCase();
      if (!canonical || seen.has(key)) continue;
      seen.add(key);
      next.push(canonical);
    }
    return next;
  }, [all, localSelected, seedSelected]);

  const filtered = React.useMemo(
    () => (!q ? options : options.filter((n) => n.toLowerCase().includes(q))),
    [options, q]
  );
  const hideCatalog = isFrench || shouldHideUntranslatedCatalog(languageCode);

    React.useEffect(() => {
    setVisible(CHUNK);
  }, [q]);

  const shown = React.useMemo(() => {
    if (hideCatalog) {
      return filtered.filter(
        (item) =>
          COMPLETION_SAFE_MEDICATIONS.includes(item) ||
          seedSelected.includes(item) ||
          localSelected.includes(item)
      );
    }
    if (q) return filtered;            // searching = show all matches (no missing items)
    return filtered.slice(0, visible); // browsing = chunk
  }, [filtered, hideCatalog, localSelected, q, seedSelected, visible]);

  // dirty tracking
  const seedKey = React.useMemo(
    () => JSON.stringify([...seedSelected].slice().sort()),
    [seedSelected]
  );
  const sortedLocalKey = React.useMemo(
    () => JSON.stringify([...localSelected].slice().sort()),
    [localSelected]
  );
  const dirty = sortedLocalKey !== seedKey;
  const needsEhrSync = localSelected.length > 0 && sortedLocalKey !== ehrSelectedKey;
  const canSave = (dirty || needsEhrSync) && localSelected.length > 0;

  const emit = (nextArr) => onChange?.(nextArr);

  const toggle = (name) => {
    setLocalSelected((prev) => {
      const exists = prev.includes(name);
      const isSpecial = COMPLETION_SAFE_MEDICATIONS.includes(name);
      let next;
      if (exists) {
        next = prev.filter((x) => x !== name);
      } else if (isSpecial) {
        next = [name];
      } else {
        next = [
          ...prev.filter((item) => !COMPLETION_SAFE_MEDICATIONS.includes(item)),
          name,
        ];
      }
      emit(next);
      return next;
    });
  };

  const addCustom = () => {
    const raw = query.trim();
    if (!raw) return;
    const pretty = raw.replace(/\s+/g, " ").trim();
    if (!all.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
      setAll((prev) => [pretty, ...prev]);
    }
    setLocalSelected((prev) => {
      if (prev.some((n) => n.toLowerCase() === pretty.toLowerCase())) {
        return prev;
      }
      const next = [
        pretty,
        ...prev.filter((item) => !COMPLETION_SAFE_MEDICATIONS.includes(item)),
      ];
      emit(next);
      return next;
    });
    setQuery("");
  };

  const addCustomRef = React.useRef(addCustom);
  React.useEffect(() => {
    addCustomRef.current = addCustom;
  }, [addCustom]);

  const handlePlusClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addCustomRef.current();
  };

  async function handleSave() {
    if (!canSave) return;

    try {
      const current = loadPatientIntroPreview() || {};
      savePatientIntroPreview({
        ...current,
        medications: localSelected.slice(),
      });

      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (!authErr && user?.id) {
        const { error: upErr } = await supabase
          .from("patient_profiles")
          .upsert(
            {
              user_id: user.id,
              current_medications: localSelected.slice(),
            },
            { onConflict: "user_id" }
          );

        if (upErr) throw upErr;

        const { error: ehrError } = await supabase
          .from("ehr_patient_info")
          .upsert(
            {
              patient_id: user.id,
              current_medications: localSelected.slice(),
            },
            { onConflict: "patient_id" }
          );

        if (ehrError) throw ehrError;

        setEhrSelectedKey(JSON.stringify([...localSelected].slice().sort()));
        setSeedSelected(localSelected.slice());

        try {
          await refreshProfile?.();
        } catch {
          // non-fatal
        }
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        nav(PATIENT_SETTINGS_PROFILE_ROUTE);
      }, 2200);
    } catch (err) {
      console.error("[MedicationsSettings] Save error:", err);
      setSaveError(
        t(
          "settings.subflows.medications.saveError",
          isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again."
        )
      );
    }
  }

  function handleClose() {
    if (!dirty) {
      nav(PATIENT_SETTINGS_PROFILE_ROUTE);
    } else {
      setShowConfirmLeave(true);
    }
  }

    React.useEffect(() => {
    if (!sentRef.current) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((v) => Math.min(v + CHUNK, filtered.length));
        }
      },
      { rootMargin: "400px" }
    );

    io.observe(sentRef.current);
    return () => io.disconnect();
  }, [filtered.length]);

  return (
    <SettingsScreenShell>
      <TopBarCenter
        title={t("settings.subflows.medications.title", isFrench ? "Médicaments" : "Medications")}
        onBack={handleClose}
        close
        right={<SaveChip disabled={!canSave} onClick={handleSave} />}
      />

      <div className="mt-3">
        <FolaOnboardingCard role="patient" />
      </div>

      {/* Search row */}
      <div className="mt-3 flex items-center gap-3">
        <SearchIcon
          className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
          aria-hidden
        />

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t(
            "settings.subflows.medications.searchPlaceholder",
            isFrench ? "Rechercher ou ajouter" : "Search or add"
          )}
          inputMode="search"
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none border-0 focus:outline-none focus:ring-0
                     text-[16px] leading-6 placeholder:text-[rgb(var(--clr-on-surface-variant,86,106,139))]"
        />

        {!!query && (
          <TapButton
            ariaLabel={t("settings.subflows.medications.clearSearch", isFrench ? "Effacer la recherche" : "Clear search")}
            onTap={() => setQuery("")}
            className="shrink-0 grid place-items-center"
          >
            <CircleXIcon className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
          </TapButton>
        )}

        <button
          type="button"
          onMouseDown={handlePlusClick}
          onTouchStart={handlePlusClick}
          onClick={handlePlusClick}
          className="shrink-0 grid place-items-center rounded-full
                     w-8 h-8 border border-[rgba(24,50,119,0.16)]
                     hover:bg-[rgba(24,50,119,0.04)]
                     focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
          aria-label={t("settings.subflows.medications.addAria", "Add medication")}
        >
          <Plus className="h-4 w-4 text-[rgb(var(--clr-on-surface-variant,86,106,139))]" />
        </button>
      </div>

      <Divider variant="outline" full className="mt-4 mb-6" />

      {/* Card list */}
      <Card className="mt-3 relative z-10 overflow-hidden">
        {loading && (
          <div className="py-8 text-center text-body-medium text-on-surface-var">
            {t("settings.subflows.medications.loading", isFrench ? "Chargement en cours..." : "Loading... one moment please!")}
          </div>
        )}

        {!loading &&
          shown.map((name, idx) => {
            const checked = localSelected.includes(name);
            return (
              <div key={`${name}-${idx}`}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggle(name)}
                  onKeyDown={(e) =>
                    (e.key === "Enter" || e.key === " ") && toggle(name)
                  }
                  className="w-full flex items-center"
                  style={{
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                  }}
                >
                  <div className="flex-1 pl-5 pr-4 py-4 text-left">
                    <span className="text-body-large text-[rgb(var(--clr-on-surface,4,8,18))]">
                      {getMedicationDisplayLabel(name, languageCode)}
                    </span>
                  </div>
                  <div className="mr-4">
                    <Checkbox
                      checked={checked}
                      onChange={(next) => {
                        if (next !== checked) toggle(name);
                      }}
                    />
                  </div>
                </div>

                {idx < filtered.length - 1 && (
                  <Divider inset variant="outlineVariant" />
                )}
              </div>
            );
          })}

        {!loading && shown.length === 0 && (
          <div className="px-5 py-6 text-body-medium text-on-surface-var">
            {hideCatalog && !q
              ? t(
                  "settings.subflows.medications.catalogHidden",
                  isFrench
                    ? "Saisissez un médicament et appuyez sur + pour l’ajouter. Les suggestions non traduites sont masquées dans cette langue."
                    : "Type a medication and tap + to add it. Untranslated suggestions are hidden on this screen in this language.",
                )
              : t(
                  "settings.subflows.medications.noMatchesAdd",
                  isFrench ? "Aucun résultat. Appuyez sur + pour ajouter « {query} »." : "No matches. Tap + to add “{query}”.",
                  { query: query.trim() },
                )}
          </div>
        )}
      </Card>

      {!loading && <div ref={sentRef} className="h-6" />}

      {/* Unsaved-changes confirm */}
      {showConfirmLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setShowConfirmLeave(false)}
          />
          <div
            className="relative w-[312px] max-w-[560px] min-w-[288px] bg-[var(--Schemes-Surface,#FFFFFF)] rounded-[28px] overflow-hidden
                       shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_0px_0px_1px_rgba(24,50,119,0.10)]"
          >
            <div className="px-6 pt-6">
              <div className="text-headline-small text-on-surface">
                {t("settings.subflows.discardTitle", isFrench ? "Abandonner les modifications ?" : "Discard changes?")}
              </div>
              <div className="text-body-medium text-on-surface-var mt-4">
                {t(
                  "settings.subflows.medications.discardBody",
                  isFrench
                    ? "Vous avez des modifications de médicaments non enregistrées. Voulez-vous fermer sans enregistrer ?"
                    : "You have unsaved medication changes. Are you sure you want to close without saving?"
                )}
              </div>
            </div>

            <div className="p-4 flex gap-3">
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-On-Surface,#040812) 8%, transparent)",
                  color: "rgb(var(--afrimed-primary))",
                }}
                onClick={() => setShowConfirmLeave(false)}
              >
                {t("common.keepEditing", isFrench ? "Continuer la modification" : "Keep editing")}
              </button>
              <button
                className="flex-1 rounded-xl px-6 py-3 text-label-large whitespace-nowrap"
                style={{
                  background:
                    "color-mix(in oklab, var(--Schemes-Critical,#F8533A) 12%, transparent)",
                  color: "var(--Schemes-Critical,#F8533A)",
                }}
                onClick={() => {
                  setShowConfirmLeave(false);
                  nav(PATIENT_SETTINGS_PROFILE_ROUTE);
                }}
              >
                {t("common.discard", isFrench ? "Abandonner" : "Discard")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar open={saved} onClose={() => setSaved(false)} duration={2200}>
        {t("common.changesSaved", isFrench ? "Modifications enregistrées" : "Changes saved")}
      </Snackbar>

      {saveError && (
        <Snackbar
          open={Boolean(saveError)}
          onClose={() => setSaveError("")}
          duration={2800}
          variant="error"
        >
          {saveError || t("settings.subflows.medications.saveError", isFrench ? "Impossible d’enregistrer les modifications. Veuillez réessayer." : "Could not save changes. Please try again.")}
        </Snackbar>
      )}
    </SettingsScreenShell>
  );
}
